import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ArrowLeft, Sparkles, Download, MessageSquare, Phone, MessageCircle, CheckCircle, Clock as ClockIcon, Circle, AlertTriangle } from 'lucide-react';
import { API, aiApi, casesApi, fileApi } from '../lib/api';
import Timeline from '../components/Timeline';
import StatusBadge from '../components/StatusBadge';
import LocationMap from '../components/LocationMap';
import SeverityBadge from '../components/SeverityBadge';
import DocumentChecklist from '../components/DocumentChecklist';
import AdminSummaryCard from '../components/AdminSummaryCard';
import OpponentDataPanel from '../components/OpponentDataPanel';
import { toast } from 'sonner';
import { TIMELINE_STEPS, timelineLabel } from '../lib/brand';

const STATUS_OPTIONS = [
  { key: 'neu', label: 'Neu' },
  { key: 'in_bearbeitung', label: 'In Bearbeitung' },
  { key: 'reparatur', label: 'Reparatur' },
  { key: 'fertig', label: 'Fertig' },
];

export default function AdminCaseDetail() {
  const { caseId } = useParams();
  const [c, setC] = useState(null);
  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState('internal');

  const reload = useCallback(async () => {
    try {
      const [cs, fs, ns] = await Promise.all([
        API.get(`/admin/cases/${caseId}`),
        API.get(`/files/case/${caseId}`),
        API.get(`/admin/cases/${caseId}/notes`),
      ]);
      setC(cs.data);
      setFiles(fs.data);
      setNotes(ns.data);
    } catch (e) {
      toast.error('Fall konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { reload(); }, [reload]);

  const changeStatus = async (status) => {
    try {
      const { data } = await API.patch(`/admin/cases/${caseId}/status`, { status });
      setC(data);
      toast.success('Status aktualisiert');
    } catch (e) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const updateTimeline = async (key, status) => {
    try {
      const { data } = await API.patch(`/admin/cases/${caseId}/timeline`, { key, status });
      setC(data);
      toast.success('Timeline aktualisiert');
    } catch (e) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const runAI = async () => {
    setAiLoading(true);
    try {
      const { data } = await API.post(`/ai/report/${caseId}`);
      setC((p) => ({ ...p, ai_analysis: data.analysis, ai_report: data.report, ai_insurance_summary: data.insurance_summary }));
      toast.success('KI-Analyse abgeschlossen');
    } catch (e) {
      toast.error('KI-Analyse fehlgeschlagen');
    } finally {
      setAiLoading(false);
    }
  };

  const downloadPdf = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/ai/pdf/${caseId}`, '_blank');

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      const { data } = await API.post('/admin/notes', { case_id: caseId, text: noteText, type: noteType });
      setNotes((n) => [data, ...n]);
      setNoteText('');
      toast.success('Notiz hinzugefügt');
    } catch (e) {
      toast.error('Fehler');
    }
  };

  if (loading) return <div className="text-sm text-slate-600">Lade...</div>;
  if (!c) return <div className="text-sm text-slate-600">Fall nicht gefunden.</div>;

  const photos = files.filter((f) => f.category === 'photo');
  const documents = files.filter((f) => f.category !== 'photo');

  return (
    <div className="space-y-4" data-testid="admin-case-detail">
      <Link to="/admin/schadenfaelle" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Alle Schadenfälle
      </Link>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-xs text-slate-500">{c.case_number}</div>
          <h1 className="font-heading font-semibold text-2xl text-[hsl(var(--primary))]">{c.person.first_name} {c.person.last_name}</h1>
          <div className="text-sm text-slate-600">{c.vehicle.make} {c.vehicle.model} • {c.vehicle.license_plate}</div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={c.status} />
          <Select value={c.status} onValueChange={changeStatus}>
            <SelectTrigger className="w-44 h-10 rounded-xl" data-testid="admin-case-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {c.situation?.drivable === false && (
            <Card className="rounded-2xl border-amber-300 bg-amber-50" data-testid="admin-not-drivable-warning">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-900">
                  <strong>Fahrzeug laut Kunde nicht fahrbereit</strong> &mdash; manuelle Rücksprache erforderlich. Aktuell ist kein eigener Abschleppservice aktiviert.
                </div>
              </CardContent>
            </Card>
          )}
          <AdminSummaryCard caseId={caseId} initial={c.ai_admin_summary} onUpdate={(d) => setC((p) => ({ ...p, ai_admin_summary: d.summary, ai_analysis: d.analysis || p.ai_analysis }))} />
          <DocumentChecklist caseId={caseId} testid="admin-case-checklist" />

          <Tabs defaultValue="timeline">
            <TabsList>
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
              <TabsTrigger value="ai">KI</TabsTrigger>
              <TabsTrigger value="opponent">Gegner</TabsTrigger>
              <TabsTrigger value="info">Details</TabsTrigger>
              <TabsTrigger value="files">Fotos/Dokumente</TabsTrigger>
            </TabsList>

            <TabsContent value="timeline" className="mt-3">
              <Card className="rounded-2xl border-slate-200">
                <CardContent className="p-5">
                  <div className="font-heading font-semibold mb-3">Status-Timeline aktualisieren</div>
                  <ul className="divide-y divide-slate-100">
                    {(c.timeline && c.timeline.length > 0 ? c.timeline : TIMELINE_STEPS.map((t) => ({ ...t, status: 'pending' }))).map((ev) => {
                      const label = timelineLabel(ev);
                      return (
                        <li key={ev.key} className="py-3 flex items-center justify-between gap-2">
                          <div>
                            <div className="font-medium text-slate-900">{label}</div>
                            <div className="text-xs text-slate-500">Status: {ev.status}</div>
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant={ev.status === 'pending' ? 'default' : 'outline'} onClick={() => updateTimeline(ev.key, 'pending')} data-testid={`tl-${ev.key}-pending`}><Circle className="h-3.5 w-3.5" /></Button>
                            <Button size="sm" variant={ev.status === 'active' ? 'default' : 'outline'} onClick={() => updateTimeline(ev.key, 'active')} data-testid={`tl-${ev.key}-active`}><ClockIcon className="h-3.5 w-3.5" /></Button>
                            <Button size="sm" variant={ev.status === 'done' ? 'default' : 'outline'} onClick={() => updateTimeline(ev.key, 'done')} data-testid={`tl-${ev.key}-done`}><CheckCircle className="h-3.5 w-3.5" /></Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="mt-5">
                    <Timeline steps={c.timeline} dataTestId="admin-case-timeline" />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="ai" className="mt-3 space-y-3">
              <Card className="rounded-2xl border-slate-200">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-[hsl(var(--accent))]" /><div className="font-heading font-semibold">KI Schadensanalyse</div></div>
                    <div className="flex gap-2">
                      <Button onClick={runAI} disabled={aiLoading || photos.length === 0} className="bg-[hsl(var(--primary))] hover:bg-[hsl(210_73%_12%)] text-white" data-testid="admin-case-run-ai">
                        {aiLoading ? 'Analysiere...' : 'KI-Analyse starten'}
                      </Button>
                      <Button variant="outline" onClick={downloadPdf} data-testid="admin-case-pdf"><Download className="h-4 w-4 mr-1.5" /> PDF</Button>
                    </div>
                  </div>
                  {c.ai_analysis ? (
                    <div className="mt-3 text-sm space-y-2">
                      <SeverityBadge ampel={c.ai_analysis.ampel} ampelLabel={c.ai_analysis.ampel_label} gesamt={c.ai_analysis.gesamt_einschaetzung} size="lg" testid="admin-severity-badge" />
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                        <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs text-slate-500">Fahrzeug erkannt</div><div className="font-medium">{c.ai_analysis.fahrzeug_erkannt ? 'Ja' : 'Nein'}</div></div>
                        <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs text-slate-500">Schäden</div><div className="font-medium">{(c.ai_analysis.schaeden || []).length}</div></div>
                        <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs text-slate-500">Gesamt</div><div className="font-medium">{c.ai_analysis.gesamt_einschaetzung}</div></div>
                        <div className="rounded-xl bg-slate-50 p-3"><div className="text-xs text-slate-500">Fahrbereit</div><div className="font-medium">{c.ai_analysis.fahrbereit_einschaetzung}</div></div>
                      </div>
                      <ul className="space-y-1 mt-2">
                        {(c.ai_analysis.schaeden || []).map((d, i) => (
                          <li key={i}>• <b>{d.bauteil}</b> — {d.schweregrad} ({Math.round((d.konfidenz || 0) * 100)}%): {d.beschreibung}</li>
                        ))}
                      </ul>
                      {c.ai_analysis.empfehlung && <div><b>Empfehlung:</b> {c.ai_analysis.empfehlung}</div>}
                      <div className="text-[11px] text-slate-500">{c.ai_analysis.disclaimer}</div>
                    </div>
                  ) : (
                    <div className="mt-3 text-sm text-slate-600">Noch keine Analyse. Bitte starten.</div>
                  )}
                </CardContent>
              </Card>
              {c.ai_report && (
                <Card className="rounded-2xl border-slate-200"><CardContent className="p-5"><div className="font-heading font-semibold mb-2">Schadensbericht</div><pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">{c.ai_report}</pre></CardContent></Card>
              )}
              {c.ai_insurance_summary && (
                <Card className="rounded-2xl border-slate-200"><CardContent className="p-5"><div className="font-heading font-semibold mb-2">Versicherungszusammenfassung</div><pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">{c.ai_insurance_summary}</pre></CardContent></Card>
              )}
            </TabsContent>

            <TabsContent value="opponent" className="mt-3">
              <OpponentDataPanel
                value={c.opponent || {}}
                onChange={async (opp) => {
                  try {
                    await casesApi.updateOpponent(caseId, opp);
                    setC((p) => ({ ...p, opponent: opp }));
                    toast.success('Gegnerdaten gespeichert');
                  } catch (e) {
                    toast.error('Fehler beim Speichern');
                  }
                }}
              />
            </TabsContent>

            <TabsContent value="info" className="mt-3 space-y-3">
              <Card className="rounded-2xl border-slate-200">
                <CardContent className="p-5 grid sm:grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Telefon:</span> <a href={`tel:${c.person.phone}`} className="text-[hsl(var(--primary))] underline">{c.person.phone}</a></div>
                  <div><span className="text-slate-500">E-Mail:</span> {c.person.email}</div>
                  <div><span className="text-slate-500">Datum:</span> {c.accident.date} {c.accident.time}</div>
                  <div><span className="text-slate-500">Notfall:</span> {c.is_emergency ? 'Ja' : 'Nein'}</div>
                  {c.vehicle?.vin && <div><span className="text-slate-500">FIN/VIN:</span> {c.vehicle.vin}</div>}
                  {c.vehicle?.first_registration && <div><span className="text-slate-500">Erstzulassung:</span> {c.vehicle.first_registration}</div>}
                  {c.vehicle?.power_kw && <div><span className="text-slate-500">Leistung:</span> {c.vehicle.power_kw} kW</div>}
                  {c.vehicle?.fuel_type && <div><span className="text-slate-500">Kraftstoff:</span> {c.vehicle.fuel_type}</div>}
                  <div className="sm:col-span-2"><span className="text-slate-500">Beschreibung:</span> {c.accident.description}</div>
                  <div><span className="text-slate-500">Fahrbereit:</span> {c.situation.drivable ? 'Ja' : 'Nein'}</div>
                  <div><span className="text-slate-500">Polizei:</span> {c.situation.police_present ? 'Ja' : 'Nein'}</div>
                  <div><span className="text-slate-500">Unfallgegner:</span> {c.situation.other_party ? 'Ja' : 'Nein'}</div>
                  <div><span className="text-slate-500">Personenschaden:</span> {c.situation.personal_injury ? 'Ja' : 'Nein'}</div>
                </CardContent>
              </Card>
              {c.voice_transcript && (
                <Card className="rounded-2xl border-slate-200">
                  <CardContent className="p-5">
                    <div className="font-heading font-semibold mb-2">Sprach-Transkript (Original)</div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.voice_transcript}</p>
                  </CardContent>
                </Card>
              )}
              {c.location && (
                <Card className="rounded-2xl border-slate-200"><CardContent className="p-5"><div className="font-heading font-semibold mb-2">Unfallort</div><LocationMap lat={c.location.lat} lng={c.location.lng} height={260} /></CardContent></Card>
              )}
            </TabsContent>

            <TabsContent value="files" className="mt-3 space-y-3">
              {photos.length > 0 && (
                <Card className="rounded-2xl border-slate-200"><CardContent className="p-5"><div className="font-heading font-semibold mb-3">Unfallfotos ({photos.length})</div><div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{photos.map((p) => (<a key={p.id} href={fileApi.url(p.id)} target="_blank" rel="noreferrer"><img src={fileApi.url(p.id)} alt={p.filename} className="w-full h-28 object-cover rounded-xl border border-slate-200" /></a>))}</div></CardContent></Card>
              )}
              <Card className="rounded-2xl border-slate-200"><CardContent className="p-5"><div className="font-heading font-semibold mb-3">Dokumente ({documents.length})</div>{documents.length === 0 ? (<div className="text-sm text-slate-600">Keine Dokumente.</div>) : (<ul className="divide-y divide-slate-100">{documents.map((d) => (<li key={d.id} className="py-2 flex items-center justify-between gap-2"><div className="truncate"><div className="text-sm font-medium">{d.filename}</div><div className="text-xs text-slate-500">{d.category} • {Math.round((d.size || 0) / 1024)} KB</div></div><a href={fileApi.url(d.id)} target="_blank" rel="noreferrer" className="text-sm text-[hsl(var(--primary))] hover:underline">Anzeigen</a></li>))}</ul>)}</CardContent></Card>
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-4">
          <Card className="rounded-2xl border-slate-200">
            <CardContent className="p-5">
              <div className="font-heading font-semibold text-slate-900 mb-3">Schnellkontakt</div>
              <div className="flex flex-col gap-2">
                <a href={`tel:${c.person.phone}`} className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm"><Phone className="h-4 w-4" /> {c.person.phone}</a>
                <a href={`https://wa.me/${(c.person.phone || '').replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#25D366] text-white text-sm hover:opacity-95"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-200">
            <CardContent className="p-5">
              <div className="font-heading font-semibold text-slate-900 mb-3">Interne Kommunikation</div>
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  {['internal', 'call', 'whatsapp'].map((t) => (
                    <button key={t} onClick={() => setNoteType(t)} className={`px-3 py-1.5 rounded-xl border text-xs ${noteType === t ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]' : 'bg-white text-slate-800 border-slate-200'}`} data-testid={`note-type-${t}`}>{t === 'internal' ? 'Notiz' : t === 'call' ? 'Anruf' : 'WhatsApp'}</button>
                  ))}
                </div>
                <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3} placeholder="Eintrag hinzufügen..." className="rounded-xl" data-testid="note-input" />
                <Button onClick={addNote} className="bg-[hsl(var(--primary))] hover:bg-[hsl(210_73%_12%)] text-white w-full" data-testid="note-submit"><MessageSquare className="h-4 w-4 mr-1.5" /> Eintrag speichern</Button>
              </div>
              <ul className="mt-4 space-y-2">
                {notes.length === 0 ? <li className="text-sm text-slate-500">Noch keine Einträge.</li> : notes.map((n) => (
                  <li key={n.id} className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                    <div className="text-xs text-slate-500">{n.type === 'call' ? 'Anruf' : n.type === 'whatsapp' ? 'WhatsApp' : 'Notiz'} • {n.author}</div>
                    <div className="text-sm text-slate-800 whitespace-pre-wrap">{n.text}</div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
